import { useEffect } from "react";
import ClientPrivateLayout from "@/layouts/ClientPrivateLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { getRequestOptions } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";

const ChangePassword = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { toast } = useToast();

  const formSchema = z.object({
    currentPassword: z.string().min(1, t('password.currentPasswordRequired')),
    newPassword: z.string().min(1, t('password.newPasswordRequired')),
    confirmPassword: z.string().min(1, t('password.confirmPasswordRequired')),
  }).refine((data) => data.newPassword === data.confirmPassword, {
    message: t('password.passwordsDoNotMatch'),
    path: ["confirmPassword"],
  });

  type FormData = z.infer<typeof formSchema>;

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  const onSubmit = async (values: FormData) => {
    console.log("Form submitted:", values);

    try {
      let fetchURL = `${import.meta.env.VITE_BOOKING_CLIENT_ENDPOINT}` + '/' + user.id + '/change-password';
      const response = await fetch(fetchURL, getRequestOptions("POST", JSON.stringify(values), "json"));
      if (!response.ok) {
        throw new Error("Failed to add or update the booking client");
      }

      const data = await response.json();
      console.log("Response Data:", data); // Optional: Check response from API
      toast({
        title: t('password.updateSuccess'),
        description: t('password.updateSuccessMessage'),
        duration: 5000,
        className: "bg-green-500 text-white border-green-600",
      });
    } catch (error) {
      console.error("Error edit booking client:", error);
    }
  };

  useEffect(() => {
    document.title = `${t('password.changeTitle')} - FitCalendar`;
  }, []);

  return (
    <ClientPrivateLayout>
      <div className="flex-1 max-w-2xl mx-auto p-8">
        <h1 className="text-3xl font-bold mb-8">{t('password.changeTitle')}</h1>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="currentPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('password.current')} <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input type="password" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="newPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('password.new')} <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input type="password" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('password.confirm')} <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input type="password" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit">{t('common.save')}</Button>
          </form>
        </Form>
      </div>
    </ClientPrivateLayout>
  );
};

export default ChangePassword;